import React, { useState } from "react";
import { useFormikContext } from "formik";
import { toast } from "react-toastify";
import _, { set } from "lodash";
import {
  Box,
  ButtonPrimary,
  ButtonSecondary,
  HStack,
  VStack,
} from "../../../../components/utils";
import { Input, Label } from "../../../../components/shared/InputField";
import {
  EmployeeDetailsUploaded,
  NameAndId,
  TransactionDetailsData,
} from "../../../../types/Transaction";
import { addEmployeeToTransaction } from "../../../../api/TransactionApisV2";
import { Select } from "../../../../components/shared/Select";
import { useGetEmployeesForAddition } from "../../../../queries/transactionV2";
import { SearchableSelect } from "../../../../components/shared/SearchableSelect";

const AddParticipantPopUp = ({
  onClose = () => {},
}: Readonly<{ onClose: () => void }>) => {
  const a = 0;
  const formik = useFormikContext<TransactionDetailsData>();
  const [employees, setEmployees] = useState<NameAndId[]>();
  const [willingness, setWillingness] = useState<number>(0);
  const [minWillingness, setMinWillingness] = useState<number>(0);
  const { data: additionOfEmployees } = useGetEmployeesForAddition();
  const [adding, setAdding] = useState(false);

  const handleAddParticipants = () => {
    setAdding(true);

    const offerFilter = formik.values.offerFilter;
    if (
      formik.values.monetaryDetails.participantListMethod === "Upload Excel"
    ) {
      if (willingness <= 0 || minWillingness <= 0) {
        toast.error("Willingness and min willingness are mandatory");
        return;
      }
    } else {
      const employeeIds =
        employees?.map((e) => String(e.id || e.employeeId)) || [];

      const includeIds = [
        ...(offerFilter.selectedEmployeeIds || []),
        ...(employees || []).filter(
          (emp) =>
            !(offerFilter.selectedEmployeeIds || []).some(
              (sel) =>
                String(sel.id || sel.employeeId) ===
                String(emp.id || emp.employeeId)
            )
        ),
      ];
      formik.setFieldValue("offerFilter.selectedEmployeeIds", includeIds);
      offerFilter.selectedEmployeeIds = includeIds;

      const updatedExcluded = (offerFilter.excludeEmployeeIds || []).filter(
        (e) =>
          !includeIds
            .map((id) => id.id || id.employeeId)
            .includes(e.id || e.employeeId || "")
      );
      formik.setFieldValue("offerFilter.excludeEmployeeIds", updatedExcluded);
      offerFilter.excludeEmployeeIds = updatedExcluded;
    }

    try {
      addEmployeeToTransaction({
        employeesList:
          employees?.map((emp) => ({
            employeeId: emp.id,
            hrId: emp.hrId,
            overridenEligibility: willingness,
            overridenMinWillingness: minWillingness,
          })) || [],
        transactionId: formik.values.id || "",
        offerFilter,
      })
        .then((res) => {
          formik.setFieldValue("previewList", res.data.previewList);
          formik.setFieldValue("offerFilter", res.data.offerFilter);
          formik.setFieldValue(
            "selectedEmployeesList",
            res.data.selectedEmployeesList
          );
          onClose();
          toast.success("Employees added successfully!");
        })
        .catch((error) => {
          toast.error(error.response.data.errorRaw);
        });
    } catch (err) {
      toast.error("An error occurred while adding participants.");
    } finally {
      setAdding(false);
    }
  };

  return (
    <VStack className="bg-white rounded-lg shadow-lg min-w-96 min-h-96">
      <div className="p-4 text-lg font-medium border-b ">
        <h6 className="flex justify-between">
          Add Participant
          <span className="cursor-pointer" onClick={() => onClose()}>
            X
          </span>
        </h6>
      </div>
      <VStack className="gap-2">
        <VStack className="flex-1 w-full max-w-xl p-4">
          <Label> Add Participants</Label>
          <SearchableSelect
            placeholder={"--Select employees--"}
            options={
              additionOfEmployees?.map((e) => ({
                label: e.employeeName,
                value: e.employeeId,
              })) || []
            }
            value={employees?.[0]?.id || employees?.[0]?.employeeId || ""}
            onChange={(e) => {
              const selectedEmployee = additionOfEmployees?.find(
                (option) => e.target.value === option.employeeId
              );

              if (selectedEmployee) {
                setEmployees([
                  {
                    id: selectedEmployee.employeeId,
                    employeeId: selectedEmployee.employeeId,
                    name: selectedEmployee.employeeName,
                    hrId: selectedEmployee.hrId,
                  },
                ]);
              } else {
                setEmployees([]);
              }
            }}
            textGetter={(option) => option.label}
            valueGetter={(option) => option.value}
          />
        </VStack>
        {formik.values.monetaryDetails.participantListMethod ===
          "Upload Excel" && (
          <HStack className="flex-1 gap-2 px-6">
            <VStack className="flex-1 ">
              <Label className="justify-start text-sm font-normal">
                Willingness
              </Label>
              <Box>
                <Input
                  type="number"
                  value={willingness}
                  onBlur={formik.handleBlur}
                  onChange={(e) => {
                    if (!e.target.value) return;
                    setWillingness(parseInt(e.target.value, 10));
                  }}
                />
              </Box>
            </VStack>
            <VStack className="flex-1 ">
              <Label className="justify-start text-sm font-normal">
                Min Willingness
              </Label>

              <Box>
                <Input
                  type="number"
                  value={minWillingness}
                  onBlur={formik.handleBlur}
                  onChange={(e) => {
                    if (!e.target.value) return;
                    setMinWillingness(parseInt(e.target.value, 10));
                  }}
                />
              </Box>
            </VStack>
          </HStack>
        )}
        <HStack className="justify-between px-4 py-4">
          <ButtonSecondary
            onClick={() => onClose()}
            className="text-gray-400 bg-slate-50"
          >
            Cancel
          </ButtonSecondary>
          <div className="flex items-center">
            <ButtonPrimary
              disabled={adding}
              loading={adding}
              onClick={() => {
                handleAddParticipants();
              }}
            >
              Add
            </ButtonPrimary>
          </div>
        </HStack>
      </VStack>
    </VStack>
  );
};

export default AddParticipantPopUp;
